import React, { useState, useEffect } from 'react';
import {
  FaFileInvoice, FaUser, FaEnvelope, FaPhone, FaBuilding,
  FaGraduationCap, FaUsers, FaCalendar, FaMapMarkerAlt,
  FaCheckCircle, FaArrowRight, FaClock, FaShieldAlt,
  FaAward, FaChartLine, FaLightbulb
} from 'react-icons/fa';
import { FaLaptopCode } from 'react-icons/fa6';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { API_URL } from '../config/api';

const DemanderDevis = () => {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    entreprise: '',
    domaine: '',
    formation: '',
    typeFormation: '',
    nombreParticipants: '',
    dateDebut: '',
    ville: '',
    message: ''
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());
  
  // États pour les données de l'API
  const [domaines, setDomaines] = useState([]);
  const [villes, setVilles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger les domaines et villes au montage du composant
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Récupérer les domaines
        const domainesResponse = await fetch(`${API_URL}/domaines`);
        const domainesData = await domainesResponse.json();
        
        // Récupérer les villes
        const villesResponse = await fetch(`${API_URL}/villes`);
        const villesData = await villesResponse.json();
        
        if (domainesData.success) {
          setDomaines(domainesData.data);
        }
        
        if (villesData.success) {
          setVilles(villesData.data);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des données:', err);
        setError('Erreur de chargement des données');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Intersection Observer pour animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisibleSections(prev => new Set([...prev, entry.target.dataset.section]));
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    document.querySelectorAll('[data-section]').forEach(section => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const typesFormation = [
    { id: 'presentiel', nom: 'Présentiel', description: 'Formation en salle avec formateur' },
    { id: 'elearning', nom: 'E-Learning', description: 'Formation 100% en ligne' },
    { id: 'blended', nom: 'Blended Learning', description: 'Mixte présentiel et en ligne' }
  ];

  const avantages = [
    {
      icon: FaClock,
      title: 'Réponse Rapide',
      description: 'Nous vous répondons sous 24h'
    },
    {
      icon: FaShieldAlt,
      title: 'Sans Engagement',
      description: 'Devis gratuit et sans obligation'
    },
    {
      icon: FaAward,
      title: 'Sur Mesure',
      description: 'Formations adaptées à vos besoins'
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhoneChange = (value) => {
    setFormData(prev => ({
      ...prev,
      telephone: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/devis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsSubmitting(false);
        setIsSuccess(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        throw new Error(data.message || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.message || 'Une erreur est survenue. Veuillez réessayer.');
      setIsSubmitting(false);
    }
  };

  const isStepValid = (step) => {
    switch (step) {
      case 1:
        return formData.nom && formData.prenom && formData.email && formData.telephone;
      case 2:
        return formData.domaine && formData.typeFormation;
      case 3:
        return formData.nombreParticipants && formData.ville;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (isStepValid(currentStep) && currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (isSuccess) {
    return (
      <div className="devis-wrapper">
        <div className="success-container">
          <div className="success-card">
            <div className="success-icon-wrapper">
              <FaCheckCircle className="success-icon" />
            </div>
            <h1 className="success-title">Demande Envoyée avec Succès !</h1>
            <p className="success-description">
              Merci pour votre demande de devis. Notre équipe va l'examiner et vous
              contactera sous 24h pour discuter de vos besoins en formation.
            </p>
            <div className="success-info">
              <div className="info-item">
                <FaEnvelope />
                <span>Un email de confirmation a été envoyé à <strong>{formData.email}</strong></span>
              </div>
              <div className="info-item">
                <FaPhone />
                <span>Nous vous contacterons au <strong>{formData.telephone}</strong></span>
              </div>
            </div>
            <div className="success-actions">
              <button onClick={() => { 
                setIsSuccess(false); 
                setCurrentStep(1); 
                setFormData({ 
                  nom: '', prenom: '', email: '', telephone: '', 
                  entreprise: '', domaine: '', formation: '', typeFormation: '', 
                  nombreParticipants: '', dateDebut: '', ville: '', message: '' 
                }); 
              }} className="btn-nouvelle-demande">
                Nouvelle Demande
              </button>
              <button onClick={() => window.location.href = '/'} className="btn-accueil">
                Retour à l'Accueil
              </button>
            </div>
          </div>
        </div>

        <style>{styles}</style>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="devis-wrapper">
        {/* Hero Section */}
        <section className="devis-hero">
          <div className="hero-background">
            <div className="hero-shape shape-1"></div>
            <div className="hero-shape shape-2"></div>
          </div>

          <div className="devis-container">
            <div className="hero-content">
              <h1 className="hero-title">
                Demandez votre <br />
                <span className="gradient-text">Devis Personnalisé</span>
              </h1>

              <p className="hero-description">
                Obtenez un devis adapté à vos besoins de formation en quelques minutes.
                Notre équipe d'experts vous accompagne dans votre projet de développement des compétences.
              </p>

              <div className="hero-stats">
                <div className="hero-stat">
                  <FaClock />
                  <div>
                    <div className="stat-value">24h</div>
                    <div className="stat-label">Réponse rapide</div>
                  </div>
                </div>
                <div className="hero-stat">
                  <FaAward />
                  <div>
                    <div className="stat-value">500+</div>
                    <div className="stat-label">Formations</div>
                  </div>
                </div>
                <div className="hero-stat">
                  <FaChartLine />
                  <div>
                    <div className="stat-value">95%</div>
                    <div className="stat-label">Satisfaction</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Form Section */}
        <section className="form-section">
          <div className="devis-container">
            <div className="form-layout">
              {/* Left - Form */}
              <div className="form-card">
                {/* Affichage des erreurs */}
                {error && (
                  <div style={{
                    padding: '16px',
                    marginBottom: '24px',
                    background: '#fee2e2',
                    border: '2px solid #ef4444',
                    borderRadius: '12px',
                    color: '#991b1b',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    {error}
                  </div>
                )}

                {/* Progress Steps */}
                <div className="progress-steps">
                  <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                    <div className="step-number">1</div>
                    <div className="step-label">Informations</div>
                  </div>
                  <div className="step-line"></div>
                  <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
                    <div className="step-number">2</div>
                    <div className="step-label">Formation</div>
                  </div>
                  <div className="step-line"></div>
                  <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
                    <div className="step-number">3</div>
                    <div className="step-label">Détails</div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="devis-form">
                  {/* Step 1 - Informations Personnelles */}
                  {currentStep === 1 && (
                    <div className="form-step">
                      <h3 className="step-title">Vos Informations</h3>
                      <p className="step-description">Commencez par nous parler de vous</p>

                      <div className="form-row">
                        <div className="form-group">
                          <label>
                            <FaUser />
                            Nom *
                          </label>
                          <input
                            type="text"
                            name="nom"
                            value={formData.nom}
                            onChange={handleChange}
                            placeholder="Votre nom"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>
                            <FaUser />
                            Prénom *
                          </label>
                          <input
                            type="text"
                            name="prenom"
                            value={formData.prenom}
                            onChange={handleChange}
                            placeholder="Votre prénom"
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>
                          <FaEnvelope />
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="votre.email@exemple.com"
                          required
                        />
                      </div>

                      <div className="form-group phone-input-wrapper">
                        <label>
                          <FaPhone />
                          Téléphone *
                        </label>
                        <PhoneInput
                          country={'bj'}
                          value={formData.telephone}
                          onChange={handlePhoneChange}
                          enableSearch={true}
                          searchPlaceholder="Rechercher un pays..."
                          preferredCountries={['bj', 'tg', 'ne', 'ci', 'sn', 'ml', 'bf', 'ga', 'cm', 'cg', 'cd', 'ma', 'dz', 'tn', 'fr', 'be', 'ch', 'ca']}
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          <FaBuilding />
                          Entreprise
                        </label>
                        <input
                          type="text"
                          name="entreprise"
                          value={formData.entreprise}
                          onChange={handleChange}
                          placeholder="Nom de votre entreprise"
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 2 - Choix de la Formation */}
                  {currentStep === 2 && (
                    <div className="form-step">
                      <h3 className="step-title">Votre Formation</h3>
                      <p className="step-description">Sélectionnez le domaine et le type de formation</p>

                      <div className="form-group">
                        <label>
                          <FaGraduationCap />
                          Domaine de Formation *
                        </label>
                        <select
                          name="domaine"
                          value={formData.domaine}
                          onChange={handleChange}
                          required
                          disabled={loading}
                        >
                          <option value="">
                            {loading ? 'Chargement...' : 'Choisissez un domaine'}
                          </option>
                          {domaines.map(domaine => (
                            <option key={domaine.id} value={domaine.id}>
                              {domaine.nom}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>
                          <FaGraduationCap />
                          Formation Spécifique
                        </label>
                        <input
                          type="text"
                          name="formation"
                          value={formData.formation}
                          onChange={handleChange}
                          placeholder="Précisez la formation (optionnel)"
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          <FaLaptopCode />
                          Type de Formation *
                        </label>
                        <div className="type-formation-grid">
                          {typesFormation.map(type => (
                            <div
                              key={type.id}
                              className={`type-card ${formData.typeFormation === type.id ? 'selected' : ''}`}
                              onClick={() => setFormData(prev => ({ ...prev, typeFormation: type.id }))}
                            >
                              <div className="type-radio">
                                {formData.typeFormation === type.id && <div className="radio-dot"></div>}
                              </div>
                              <div className="type-content">
                                <div className="type-name">{type.nom}</div>
                                <div className="type-description">{type.description}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 3 - Détails */}
                  {currentStep === 3 && (
                    <div className="form-step">
                      <h3 className="step-title">Détails de la Formation</h3>
                      <p className="step-description">Finalisez votre demande</p>

                      <div className="form-row">
                        <div className="form-group">
                          <label>
                            <FaUsers />
                            Nombre de Participants *
                          </label>
                          <input
                            type="number"
                            name="nombreParticipants"
                            value={formData.nombreParticipants}
                            onChange={handleChange}
                            placeholder="Entrez le nombre"
                            min="1"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>
                            <FaMapMarkerAlt />
                            Ville *
                          </label>
                          <select
                            name="ville"
                            value={formData.ville}
                            onChange={handleChange}
                            required
                            disabled={loading}
                          >
                            <option value="">
                              {loading ? 'Chargement...' : 'Choisissez une ville'}
                            </option>
                            {villes.map(ville => (
                              <option key={ville.id} value={ville.id}>
                                {ville.nom}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>
                          <FaCalendar />
                          Date de Début Souhaitée
                        </label>
                        <input
                          type="date"
                          name="dateDebut"
                          value={formData.dateDebut}
                          onChange={handleChange}
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          <FaFileInvoice />
                          Message / Besoins Spécifiques
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Décrivez vos besoins, objectifs ou questions..."
                          rows="5"
                        ></textarea>
                      </div>
                    </div>
                  )}

                  {/* Form Navigation */}
                  <div className="form-navigation">
                    {currentStep > 1 && (
                      <button type="button" onClick={prevStep} className="btn-prev">
                        Précédent
                      </button>
                    )}

                    {currentStep < 3 ? (
                      <button
                        type="button"
                        onClick={nextStep}
                        className="btn-next"
                        disabled={!isStepValid(currentStep)}
                      >
                        Suivant
                        <FaArrowRight />
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="btn-submit"
                        disabled={isSubmitting || !isStepValid(currentStep)}
                      >
                        {isSubmitting ? 'Envoi en cours...' : 'Envoyer la Demande'}
                        {!isSubmitting && <FaArrowRight />}
                      </button>
                    )}
                  </div>
                </form>
              </div>

              {/* Right - Info Sidebar */}
              <div className="info-sidebar">
                <div className="info-card">
                  <h3 className="info-title">Pourquoi CINEF ?</h3>

                  <div className="avantages-list">
                    {avantages.map((avantage, index) => {
                      const Icon = avantage.icon;
                      return (
                        <div key={index} className="avantage-item">
                          <div className="avantage-icon">
                            <Icon />
                          </div>
                          <div className="avantage-text">
                            <h4>{avantage.title}</h4>
                            <p>{avantage.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="contact-card">
                  <h4>Besoin d'Aide ?</h4>
                  <p>Notre équipe est disponible pour vous accompagner</p>
                  <div className="contact-info">
                    <div className="contact-item">
                      <FaPhone />
                      <span>+229 63 18 60 60</span>
                    </div>
                    <div className="contact-item">
                      <FaEnvelope />
                      <span>info@cinef-bj.com</span>
                    </div>
                  </div>
                </div>

                <div className="trust-badge">
                  <FaShieldAlt className="shield-icon" />
                  <p>Vos données sont sécurisées et ne seront jamais partagées</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <style>{styles}</style>
      </div>
      <Footer />
    </>
  );
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .devis-wrapper * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  .devis-wrapper {
    font-family: 'Inter', sans-serif;
    background: #fafafa;
    min-height: 100vh;
  }

  .devis-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 24px;
  }

  /* Hero Section */
  .devis-hero {
    min-height: 50vh;
    display: flex;
    align-items: center;
   background: linear-gradient(135deg, #198754 0%, #20c997 100%);
    padding: 120px 0 80px;
    position: relative;
    overflow: hidden;
  }

  .hero-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 0;
  }

  .hero-shape {
    position: absolute;
    border-radius: 50%;
    opacity: 0.6;
    animation: floatShape 20s ease-in-out infinite;
  }

  .shape-1 {
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(253, 126, 20, 0.1) 0%, transparent 70%);
    top: -10%;
    right: -10%;
  }

  .shape-2 {
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(25, 135, 84, 0.1) 0%, transparent 70%);
    bottom: -5%;
    left: -5%;
    animation-delay: -5s;
  }

  @keyframes floatShape {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(30px, -30px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
  }

  .hero-content {
    text-align: center;
    position: relative;
    z-index: 1;
    max-width: 900px;
    margin: 0 auto;
  }

  .hero-title {
    font-size: 52px;
    font-weight: 900;
    line-height: 1.1;
    color: white;
    margin-bottom: 20px;
  }

  .gradient-text {
    background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .hero-description {
    font-size: 18px;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 40px;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
  }

  .hero-stats {
    display: flex;
    gap: 48px;
    justify-content: center;
    flex-wrap: wrap;
  }

  .hero-stat {
    display: flex;
    align-items: center;
    gap: 12px;
    color: white;
  }

  .hero-stat svg {
    font-size: 32px;
    color: #fd7e14;
  }

  .stat-value {
    font-size: 28px;
    font-weight: 900;
    line-height: 1;
  }

  .stat-label {
    font-size: 13px;
    opacity: 0.8;
  }

  /* Form Section */
  .form-section {
    padding: 80px 0;
    background: #fafafa;
  }

  .form-layout {
    display: grid;
    grid-template-columns: 1.5fr 1fr;
    gap: 40px;
    align-items: start;
  }

  .form-card {
    background: white;
    border-radius: 24px;
    padding: 48px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }

  /* Progress Steps */
  .progress-steps {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 48px;
    position: relative;
  }

  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    z-index: 2;
  }

  .step-number {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #e5e7eb;
    color: #9ca3af;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 700;
    transition: all 0.3s ease;
  }

  .step.active .step-number {
    background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
    color: white;
    box-shadow: 0 8px 20px rgba(253, 126, 20, 0.3);
  }

  .step.completed .step-number {
    background: #198754;
    color: white;
  }

  .step-label {
    font-size: 14px;
    font-weight: 600;
    color: #9ca3af;
  }

  .step.active .step-label {
    color: #1a202c;
  }

  .step-line {
    flex: 1;
    height: 3px;
    background: #e5e7eb;
    margin: 0 12px;
    position: relative;
    top: -20px;
  }

  /* Form */
  .devis-form {
  }

  .form-step {
    animation: fadeIn 0.5s ease;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .step-title {
    font-size: 28px;
    font-weight: 800;
    color: #1a202c;
    margin-bottom: 8px;
  }

  .step-description {
    font-size: 15px;
    color: #6b7280;
    margin-bottom: 32px;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
  }

  .form-group {
    margin-bottom: 20px;
  }

  .form-group label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
  }

  .form-group label svg {
    font-size: 16px;
    color: #fd7e14;
  }

  .form-group input,
  .form-group select,
  .form-group textarea {
    width: 100%;
    padding: 14px 16px;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    font-size: 15px;
    font-family: inherit;
    transition: all 0.3s ease;
    background: white;
  }

  .form-group input:focus,
  .form-group select:focus,
  .form-group textarea:focus {
    outline: none;
    border-color: #fd7e14;
    box-shadow: 0 0 0 4px rgba(253, 126, 20, 0.1);
  }

  .form-group textarea {
    resize: vertical;
    min-height: 120px;
  }

  /* Phone Input Custom Styling */
  .phone-input-wrapper .react-tel-input {
    font-family: inherit;
  }

  .phone-input-wrapper .react-tel-input .form-control {
    width: 100%;
    padding: 16px 18px 16px 58px;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    font-size: 15px;
    font-family: inherit;
    transition: all 0.3s ease;
    background: white;
    height: auto;
    color: #2d3748;
    font-weight: 500;
  }

  .phone-input-wrapper .react-tel-input .form-control:focus {
    outline: none;
    border-color: #fd7e14;
    box-shadow: 0 0 0 4px rgba(253, 126, 20, 0.1);
  }

  .phone-input-wrapper .react-tel-input .flag-dropdown {
    border: none;
    background: transparent;
  }

  .phone-input-wrapper .react-tel-input .selected-flag {
    padding: 0 0 0 18px;
    border-radius: 12px 0 0 12px;
  }

  .phone-input-wrapper .react-tel-input .selected-flag:hover,
  .phone-input-wrapper .react-tel-input .selected-flag.open {
    background: transparent;
  }

  /* Type Formation Grid */
  .type-formation-grid {
    display: grid;
    gap: 16px;
    margin-top: 12px;
  }

  .type-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .type-card:hover {
    border-color: #fd7e14;
    background: #fff5f0;
  }

  .type-card.selected {
    border-color: #fd7e14;
    background: #fff5f0;
    box-shadow: 0 4px 12px rgba(253, 126, 20, 0.15);
  }

  .type-radio {
    width: 24px;
    height: 24px;
    border: 2px solid #d1d5db;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.3s ease;
  }

  .type-card.selected .type-radio {
    border-color: #fd7e14;
  }

  .radio-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #fd7e14;
  }

  .type-content {
    flex: 1;
  }

  .type-name {
    font-size: 16px;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 4px;
  }

  .type-description {
    font-size: 13px;
    color: #6b7280;
  }

  /* Form Navigation */
  .form-navigation {
    display: flex;
    gap: 16px;
    justify-content: flex-end;
    margin-top: 40px;
    padding-top: 32px;
    border-top: 2px solid #f3f4f6;
  }

  .btn-prev,
  .btn-next,
  .btn-submit {
    padding: 14px 32px;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;
    border: none;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .btn-prev {
    background: white;
    color: #374151;
    border: 2px solid #e5e7eb;
  }

  .btn-prev:hover {
    border-color: #d1d5db;
    background: #f9fafb;
  }

  .btn-next,
  .btn-submit {
    background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
    color: white;
    box-shadow: 0 8px 20px rgba(253, 126, 20, 0.3);
  }

  .btn-next:hover,
  .btn-submit:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(253, 126, 20, 0.4);
  }

  .btn-next:disabled,
  .btn-submit:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  /* Info Sidebar */
  .info-sidebar {
    position: sticky;
    top: 100px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .info-card,
  .contact-card {
    background: white;
    border-radius: 20px;
    padding: 32px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  }

  .info-title {
    font-size: 20px;
    font-weight: 800;
    color: #1a202c;
    margin-bottom: 24px;
  }

  .avantages-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .avantage-item {
    display: flex;
    gap: 16px;
  }

  .avantage-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #fff5f0 0%, #fef3c7 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fd7e14;
    font-size: 20px;
    flex-shrink: 0;
  }

  .avantage-text h4 {
    font-size: 16px;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 4px;
  }

  .avantage-text p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.5;
  }

  .contact-card h4 {
    font-size: 18px;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 8px;
  }

  .contact-card > p {
    font-size: 14px;
    color: #6b7280;
    margin-bottom: 20px;
  }

  .contact-info {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .contact-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #f9fafb;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
  }

  .contact-item svg {
    color: #fd7e14;
    font-size: 16px;
  }

  .trust-badge {
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    border-radius: 16px;
    padding: 24px;
    text-align: center;
  }

  .shield-icon {
    font-size: 32px;
    color: #198754;
    margin-bottom: 12px;
  }

  .trust-badge p {
    font-size: 13px;
    color: #374151;
    line-height: 1.6;
  }

  /* Success Page */
  .success-container {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 80px 24px;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  }

  .success-card {
    background: white;
    border-radius: 24px;
    padding: 64px 48px;
    max-width: 700px;
    text-align: center;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  }

  .success-icon-wrapper {
    width: 100px;
    height: 100px;
    margin: 0 auto 32px;
    background: linear-gradient(135deg, #198754 0%, #20c997 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: bounceIn 0.8s ease;
  }

  @keyframes bounceIn {
    0% { transform: scale(0); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }

  .success-icon {
    font-size: 52px;
    color: white;
  }

  .success-title {
    font-size: 36px;
    font-weight: 900;
    color: #1a202c;
    margin-bottom: 16px;
  }

  .success-description {
    font-size: 17px;
    color: #6b7280;
    line-height: 1.7;
    margin-bottom: 40px;
  }

  .success-info {
    background: #f8f9fa;
    border-radius: 16px;
    padding: 32px;
    margin-bottom: 32px;
  }

  .info-item {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 0;
    font-size: 15px;
    color: #374151;
    text-align: left;
  }

  .info-item:not(:last-child) {
    border-bottom: 1px solid #e5e7eb;
  }

  .info-item svg {
    color: #fd7e14;
    font-size: 20px;
    flex-shrink: 0;
  }

  .info-item strong {
    color: #1a202c;
  }

  .success-actions {
    display: flex;
    gap: 16px;
    justify-content: center;
    flex-wrap: wrap;
  }

  .btn-nouvelle-demande,
  .btn-accueil {
    padding: 16px 36px;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;
    border: none;
  }

  .btn-nouvelle-demande {
    background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
    color: white;
    box-shadow: 0 8px 20px rgba(253, 126, 20, 0.3);
  }

  .btn-nouvelle-demande:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(253, 126, 20, 0.4);
  }

  .btn-accueil {
    background: white;
    color: #374151;
    border: 2px solid #e5e7eb;
  }

  .btn-accueil:hover {
    border-color: #d1d5db;
    background: #f9fafb;
  }

  /* Responsive */
  @media (max-width: 1024px) {
    .form-layout {
      grid-template-columns: 1fr;
    }

    .info-sidebar {
      position: static;
    }

    .hero-title {
      font-size: 42px;
    }
  }

  @media (max-width: 768px) {
    .devis-hero {
      padding: 100px 0 60px;
      min-height: auto;
    }

    .hero-title {
      font-size: 36px;
    }

    .hero-description {
      font-size: 16px;
    }

    .hero-stats {
      gap: 24px;
    }

    .form-card {
      padding: 32px 24px;
    }

    .form-row {
      grid-template-columns: 1fr;
    }

    .progress-steps {
      margin-bottom: 32px;
    }

    .step-label {
      font-size: 12px;
    }

    .step-number {
      width: 40px;
      height: 40px;
      font-size: 16px;
    }

    .step-line {
      margin: 0 8px;
    }

    .form-navigation {
      flex-direction: column-reverse;
    }

    .btn-prev,
    .btn-next,
    .btn-submit {
      width: 100%;
      justify-content: center;
    }

    .success-card {
      padding: 48px 32px;
    }

    .success-title {
      font-size: 28px;
    }

    .success-actions {
      flex-direction: column;
    }

    .btn-nouvelle-demande,
    .btn-accueil {
      width: 100%;
    }
  }

  @media (max-width: 640px) {
    .devis-container {
      padding: 0 16px;
    }

    .hero-title {
      font-size: 28px;
    }

    .hero-stats {
      flex-direction: column;
      gap: 16px;
    }

    .step-title {
      font-size: 24px;
    }

    .info-card,
    .contact-card {
      padding: 24px;
    }
  }
`;

export default DemanderDevis;